import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../theme';

interface KPICardProps {
  title: string;
  value: string | number;
  icon: keyof typeof Ionicons.glyphMap;
  color: string;
  subtitle?: string;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  onPress?: () => void;
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
  trend,
  onPress,
}) => {
  const isWeb = Platform.OS === 'web';

  return (
    <TouchableOpacity
      style={[
        styles.card,
        { borderLeftColor: color },
        isWeb && styles.cardWeb,
      ]}
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
      disabled={!onPress}
    >
      <View style={styles.iconContainer}>
        <Ionicons name={icon} size={32} color={color} />
      </View>
      
      <View style={styles.content}>
        <Text style={styles.title}>{title}</Text>
        <View style={styles.valueRow}>
          <Text style={[styles.value, { color }]}>{value}</Text>
          {trend && (
            <View style={[styles.trendBadge, { backgroundColor: trend.direction === 'up' ? '#e8f5e9' : '#ffebee' }]}>
              <Ionicons 
                name={trend.direction === 'up' ? 'trending-up' : 'trending-down'} 
                size={14} 
                color={trend.direction === 'up' ? theme.colors.green600 : theme.colors.red600} 
              />
              <Text style={[styles.trendText, { color: trend.direction === 'up' ? theme.colors.green600 : theme.colors.red600 }]}>
                {Math.abs(trend.value)}%
              </Text>
            </View>
          )}
        </View>
        {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.md,
    borderLeftWidth: 4,
    flexDirection: 'row',
    alignItems: 'center',
    ...theme.shadows.md,
    width: '48%',
  },
  cardWeb: {
    width: 'auto',
    flex: 1,
    minWidth: 220,
    marginRight: theme.spacing.md,
  },
  iconContainer: {
    marginRight: theme.spacing.md,
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.md,
    backgroundColor: theme.colors.gray50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  value: {
    fontSize: theme.typography.sizes.xxl,
    fontWeight: '700' as '700',
    marginRight: theme.spacing.sm,
  },
  subtitle: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
  },
  trendBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: theme.borderRadius.sm,
  },
  trendText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '600' as '600',
    marginLeft: 2,
  },
});
